import { useTranslate } from 'react-polyglot';
import { observer } from 'mobx-react';
import { useTheme } from 'styled-components';

import {
  BaseModal,
  BasicParagraph,
  BasicSubtitle,
  BiggerSubtitle,
  Button,
  Tooltip,
} from '@genially/design-system';
import { LiveIcon } from '@genially/icons';
import type { AliasMode, ResultsMode } from '@genially/p2p-lib';

import { AliasModeSelector } from './components/AliasModeSelector/AliasModeSelector';
import { HostCursorToggle } from './components/HostCursorToggle/HostCursorToggle';
import { RaiseHandToggle } from './components/RaiseHandToggle/RaiseHandToggle';
import { ReactionsToggle } from './components/ReactionsToggle/ReactionsToggle';
import { ResultsModeSelector } from './components/ResultsModeSelector/ResultsModeSelector';
import {
  Content,
  FooterContent,
  Header,
  SectionHeader,
  SectionHint,
  SubsectionHeader,
  Toggles,
} from './SessionConfigModal.styled';

/**
 * An observer: the host opens it through ModalProvider, which snapshots the element and
 * sits above HostGameProvider, so nothing re-renders it from there. The config props read
 * live values instead, and this is what picks the changes up.
 */
export const SessionConfigModal = observer(
  ({
    parentElement,
    closeModal,
    isInitialConfig = false,
    reactionsConfig,
    raiseHandConfig,
    hostCursorConfig,
    resultsModeConfig,
    aliasModeConfig,
    showReactionsToggle = true,
    showRaiseHandToggle = true,
    showHostCursorToggle = false,
    showAnalyticsResultsMode = false,
    showNoResultsMode = false,
    showAliasMode = false,
  }: {
    parentElement: HTMLElement;
    closeModal: () => void;
    /**
     * The very first time this modal is shown on a new session (behind a feature flag): it
     * can only be dismissed via the "Empezar la sesión" button (no X, no outside click), the
     * title changes, and a hint about editing the config later is shown next to the button.
     */
    isInitialConfig?: boolean;
    reactionsConfig: { isEnabled: boolean; toggle: () => void };
    raiseHandConfig: { isEnabled: boolean; toggle: () => void };
    hostCursorConfig: { isEnabled: boolean; toggle: () => void };
    resultsModeConfig: { mode: ResultsMode; setMode: (mode: ResultsMode) => void };
    aliasModeConfig: { mode: AliasMode; setMode: (mode: AliasMode) => void };
    showReactionsToggle?: boolean;
    showRaiseHandToggle?: boolean;
    showHostCursorToggle?: boolean;
    showAnalyticsResultsMode?: boolean;
    showNoResultsMode?: boolean;
    showAliasMode?: boolean;
  }) => {
    const t = useTranslate();

    const theme = useTheme();

    // Ranking on its own is not a choice, so the whole section only makes sense once at
    // least one of the alternative modes is available.
    const showResultsMode = showAnalyticsResultsMode || showNoResultsMode;

    // Without the results mode section the modal is just the old settings list, so the
    // toggles stack one per row in a narrow modal instead of spreading across it.
    const toggleColumns = showResultsMode
      ? [showReactionsToggle, showRaiseHandToggle, showHostCursorToggle].filter(Boolean)
          .length
      : 1;

    return (
      <BaseModal
        close={isInitialConfig ? undefined : closeModal}
        shouldCloseOnOverlayClick={!isInitialConfig}
        parentElement={parentElement}
        width={'100%'}
        maxWidth={theme.size.units(showResultsMode ? 110 : 60)}
      >
        <BaseModal.Header>
          <Header>
            <BiggerSubtitle>
              {t(
                isInitialConfig
                  ? '__new.play.shared.configuration.modal.initialTitle'
                  : '__new.play.shared.configuration.modal.title',
              )}
            </BiggerSubtitle>
            {showResultsMode && (
              <BasicParagraph>
                {t('__new.play.shared.configuration.modal.subtitle')}
              </BasicParagraph>
            )}
          </Header>
        </BaseModal.Header>
        <BaseModal.Content>
          <Content>
            {showAliasMode && (
              <>
                <SubsectionHeader>
                  <BasicSubtitle>
                    {t('__new.play.shared.configuration.modal.aliasMode.title')}
                  </BasicSubtitle>
                </SubsectionHeader>

                <Tooltip
                  text={t(
                    '__new.play.shared.configuration.modal.aliasMode.disabledTooltip',
                  )}
                  disabled={isInitialConfig}
                  variant={Tooltip.Variant.DARK}
                  placement="top"
                >
                  <AliasModeSelector
                    disabled={!isInitialConfig}
                    mode={aliasModeConfig.mode}
                    onChange={aliasModeConfig.setMode}
                  />
                </Tooltip>
              </>
            )}

            {showResultsMode && (
              <>
                <SectionHeader>
                  <BasicSubtitle>
                    {t('__new.play.shared.configuration.modal.resultsMode.title')}
                  </BasicSubtitle>
                  <SectionHint>
                    {t('__new.play.shared.configuration.modal.resultsMode.hint')}
                  </SectionHint>
                </SectionHeader>

                <ResultsModeSelector
                  mode={resultsModeConfig.mode}
                  onChange={resultsModeConfig.setMode}
                  showAnalyticsOption={showAnalyticsResultsMode}
                  showNoResultsOption={showNoResultsMode}
                />
              </>
            )}

            <SubsectionHeader>
              <BasicSubtitle>
                {t('__new.play.shared.configuration.modal.interactionSettings.title')}
              </BasicSubtitle>
            </SubsectionHeader>

            <Toggles $columns={toggleColumns}>
              {showReactionsToggle && (
                <ReactionsToggle
                  isEnabled={reactionsConfig.isEnabled}
                  onChange={reactionsConfig.toggle}
                />
              )}
              {showRaiseHandToggle && (
                <RaiseHandToggle
                  isEnabled={raiseHandConfig.isEnabled}
                  onChange={raiseHandConfig.toggle}
                />
              )}
              {showHostCursorToggle && (
                <HostCursorToggle
                  isEnabled={hostCursorConfig.isEnabled}
                  onChange={hostCursorConfig.toggle}
                />
              )}
            </Toggles>
          </Content>
        </BaseModal.Content>
        {isInitialConfig && (
          <BaseModal.Footer>
            <FooterContent>
              <Button
                size={Button.Size.BIG}
                variant={Button.Variant.ACTION}
                StartIcon={<LiveIcon />}
                onClick={closeModal}
              >
                {t('__new.play.shared.configuration.modal.startSession')}
              </Button>
            </FooterContent>
          </BaseModal.Footer>
        )}
      </BaseModal>
    );
  },
);
